import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Instagram, Linkedin, Facebook, ArrowUp } from 'lucide-react';

export function Footer() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';

  const getLocalizedHref = (href: string) => `/${currentLang}${href}`;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const footerLinks = {
    products: [
      { label: t('products.categories.kapsul-ev', 'Kapsül Ev'), href: '/products/kapsul-ev' },
      { label: t('products.categories.bungalov', 'Bungalov'), href: '/products/bungalov' },
      { label: t('products.categories.prefabrik-ev', 'Prefabrik Ev'), href: '/products/prefabrik-ev' },
      { label: t('products.categories.moduler-ev', 'Modüler Ev'), href: '/products/moduler-ev' },
    ],
    company: [
      { key: 'footer.about', href: '/about' },
      { key: 'footer.process', href: '/process' },
      { key: 'footer.blog', href: '/blog' },
      { key: 'footer.faq', href: '/faq' },
    ],
    support: [
      { key: 'footer.contact', href: '/contact' },
      { key: 'footer.warranty', href: '/warranty' },
      { key: 'footer.shipping', href: '/shipping' },
      { key: 'footer.returns', href: '/returns' },
    ],
  };

  return (
    <footer className="bg-earth-900 text-white" role="contentinfo">
      {/* Main Footer */}
      <div className="container-custom py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2 space-y-6">
            <Link to={`/${currentLang}`} className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="Muttiny Tiny House" 
                className="h-12 w-auto object-contain brightness-0 invert"
              />
            </Link>
            
            <p className="text-earth-300 max-w-sm">
              {t('footer.description')}
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a href="mailto:info@muttinyhouse.com" className="flex items-center gap-3 text-earth-300 hover:text-white transition-colors">
                <Mail className="w-5 h-5" />
                <span>info@muttinyhouse.com</span>
              </a>
              <a href="tel:+901234567890" className="flex items-center gap-3 text-earth-300 hover:text-white transition-colors">
                <Phone className="w-5 h-5" />
                <span>+90 123 456 78 90</span>
              </a>
              <div className="flex items-start gap-3 text-earth-300">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>{t('footer.address')}</span>
              </div>
            </div>
            
            {/* Social Links */}
            <div className="flex items-center gap-4 pt-2">
              <a 
                href="https://instagram.com/muttinyhouse" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-earth-800 flex items-center justify-center text-earth-300 hover:bg-brand-600 hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://linkedin.com/company/muttiny" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-earth-800 flex items-center justify-center text-earth-300 hover:bg-brand-600 hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://facebook.com/muttinyhouse" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-earth-800 flex items-center justify-center text-earth-300 hover:bg-brand-600 hover:text-white transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Products Column */}
          <div>
            <h3 className="font-semibold text-white mb-4">{t('footer.products')}</h3>
            <ul className="space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={getLocalizedHref(link.href)}
                    className="text-earth-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="font-semibold text-white mb-4">{t('footer.company')}</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={getLocalizedHref(link.href)}
                    className="text-earth-400 hover:text-white transition-colors"
                  >
                    {t(link.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Column */}
          <div>
            <h3 className="font-semibold text-white mb-4">{t('footer.support')}</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={getLocalizedHref(link.href)}
                    className="text-earth-400 hover:text-white transition-colors"
                  >
                    {t(link.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-earth-800">
        <div className="container-custom py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-earth-400 text-sm text-center md:text-left">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
          
          <div className="flex items-center gap-6">
            <Link to={getLocalizedHref('/privacy')} className="text-sm text-earth-400 hover:text-white transition-colors">
              {t('footer.privacy')}
            </Link>
            <Link to={getLocalizedHref('/terms')} className="text-sm text-earth-400 hover:text-white transition-colors">
              {t('footer.terms')}
            </Link>
            <button
              onClick={scrollToTop}
              className="flex items-center gap-2 text-sm text-earth-400 hover:text-white transition-colors"
              aria-label={t('footer.backToTop')}
            >
              <ArrowUp className="w-4 h-4" />
              {t('footer.backToTop')}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
